import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, from, map, switchMap, throwError } from 'rxjs';

import { runtimeConfig } from '../runtime-config';
import { decodeMessageBody } from './decode-message-body';
import {
  Message,
  Request,
  Session,
  SessionFilters,
  SessionResponse,
  StatDimension,
  StatsResponse,
  Trace,
  TracerResponse,
} from '../models/tracer.model';

/** The most requests GET /v2/sessions/{id} returns (`limit`, capped server-side). */
const SESSION_TREE_MAX = 500;

/** The tracer answered 404: no such session or request, or not the caller's to see. */
export function isNotFound(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 404;
}

/**
 * Client for the agw-tracer-v2 read API (docs/read-api.md).
 *
 * Also the ADAPTER between the v2 wire format and the view model the UI is
 * written against. The v2 API returns the nesting explicitly — an inbound
 * request with its airline calls underneath — while the templates expect a flat
 * `Request.Traces[]` discriminated by `Source`. Mapping here rather than
 * rewriting every template keeps the v1 UI intact, and the nesting is still
 * correct because it comes from the server rather than being inferred.
 */
@Injectable({ providedIn: 'root' })
export class TracerService {
  private readonly http = inject(HttpClient);
  private readonly base = runtimeConfig.tracerUrl;

  // ---------------------------------------------------------------- sessions

  /**
   * One page of sessions, newest first.
   *
   * Pagination is by CURSOR: the API uses a keyset so page N costs the same as
   * page 1. Callers pass `next_cursor` from the previous response to advance.
   */
  getTracerData(filters: SessionFilters = {}): Observable<TracerResponse> {
    let params = new HttpParams();
    const add = (k: string, v: string | number | boolean | undefined) => {
      if (v !== undefined && v !== '' && v !== false) params = params.set(k, String(v));
    };
    add('from', filters.from);
    add('to', filters.to);
    add('application', filters.application);
    add('consumer', filters.consumer);
    add('agency', filters.agency);
    add('agent', filters.agent);
    add('method', filters.method);
    add('provider', filters.provider);
    add('status', filters.status);
    add('anonymous', filters.anonymous);
    add('cursor', filters.cursor);
    add('limit', filters.limit);

    return this.http
      .get<{ sessions: V2SessionListItem[]; next_cursor?: string }>(`${this.base}/v2/sessions`, {
        params,
        withCredentials: true,
      })
      .pipe(
        map((r) => ({
          sessions: (r.sessions ?? []).map(toSession),
          next_cursor: r.next_cursor,
        })),
      );
  }

  /**
   * What the search box runs: the query as a session id, else as a request id.
   *
   * A request id opens its whole session with `matchedRequestId` set, so the
   * view can select that request. The tree is asked for at the API's maximum
   * there, so the request is in it unless its session is extraordinarily long.
   * When neither lookup knows the id the error is the 404 — see isNotFound —
   * which callers show as "no results", not as a failure.
   */
  search(query: string): Observable<SessionResponse> {
    return this.getSessionData(query).pipe(
      catchError((err) => {
        if (!isNotFound(err)) return throwError(() => err);
        return this.findRequest(query).pipe(
          switchMap((found) => this.getSessionData(found.session_id, SESSION_TREE_MAX)),
          map((res) => ({ ...res, matchedRequestId: query })),
        );
      }),
    );
  }

  /** The full tree for one session, flattened into the v1 view model. */
  getSessionData(sessionId: string, limit?: number): Observable<SessionResponse> {
    let params = new HttpParams();
    if (limit) params = params.set('limit', limit);
    return this.http
      .get<V2SessionTree>(`${this.base}/v2/sessions/${encodeURIComponent(sessionId)}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        map((r) => {
          const requests = (r.requests ?? []).map((req, i) => toRequest(req, i));
          const providers = [
            ...new Set(
              requests.flatMap((rq) =>
                rq.Traces.filter((t) => t.Source === 'requester').map((t) => t.Providers),
              ),
            ),
          ].filter(Boolean);

          return {
            session: {
              id: 0,
              value: r.session_id,
              created_at: requests[0]?.CreatedAt ?? '',
              updated_at: requests[requests.length - 1]?.CreatedAt ?? '',
              requests,
              important: false,
              status: 'clean',
            } as Session,
            requests,
            providers,
          };
        }),
      );
  }

  /**
   * The whole session as a zip, in order: every request and airline call with
   * bodies, headers and a summary.json of UTC times. Scoped server-side like
   * the session itself.
   *
   * `provider` keeps only that airline's calls (and the requests that made
   * them); `scope: 'airline'` keeps only the airline request/response files —
   * together, what to send to that airline.
   */
  downloadSessionZip(sessionId: string, filter: SessionZipFilter = {}): Observable<Blob> {
    let params = new HttpParams();
    if (filter.provider) params = params.set('provider', filter.provider);
    if (filter.scope) params = params.set('scope', filter.scope);
    return this.http.get(`${this.base}/v2/sessions/${encodeURIComponent(sessionId)}/zip`, {
      params,
      responseType: 'blob',
      withCredentials: true,
    });
  }

  /** Resolve a request id to its session, so a search can jump to the tree. */
  findRequest(requestId: string): Observable<{ request_id: string; session_id: string }> {
    return this.http.get<{ request_id: string; session_id: string }>(
      `${this.base}/v2/requests/${encodeURIComponent(requestId)}`,
      { withCredentials: true },
    );
  }

  // ------------------------------------------------------------------ bodies

  /**
   * Fetch and decode one body.
   *
   * `url` comes straight from a Message.BodyUrl — already the correct path, so
   * callers never build it. The response is the stored `brotli_<base64>` form;
   * decodeMessageBody unwraps that and any inner gzip.
   */
  getBody(url: string): Observable<string> {
    return this.http
      .get(`${this.base}${url}`, { responseType: 'text', withCredentials: true })
      .pipe(switchMap((stored) => from(decodeMessageBody(stored))));
  }

  /**
   * Fetch one message body by its URL, in the response shape the v1 detail view
   * expects.
   *
   * v1's /message/:id implemented a three-tier size protocol — inline body,
   * presigned URL, or "too big, use the ZIP" — because bodies were served
   * through the API. v2 serves them straight from object storage via
   * /v2/bodies/{ref}, so there is no tiering: the body always comes back.
   *
   * The `size` / `zip_only` fields are kept so the existing template logic
   * still compiles and behaves sensibly; zip_only is simply never true.
   */
  getMessage(bodyUrl: string): Observable<V1MessageResponse> {
    return this.getBody(bodyUrl).pipe(
      map((body) => ({
        body,
        size: body.length,
        zip_only: false,
        display_on_demand: false,
        presigned_url: '',
        warning: '',
        // v2 carries headers on the session tree (Message.Headers), not with
        // the body; the detail view reads them from the message.
        headers: {} as Record<string, string>,
        error: undefined as string | undefined,
      })),
    );
  }

  /**
   * Fetch a body from a presigned object-storage URL.
   *
   * v1 used this for its on-demand tier, where large bodies were served by a
   * presigned URL rather than inline. v2 has no tiering, so this is only
   * reachable from legacy code paths; it fetches the URL directly.
   */
  getMessageFromPresignedUrl(url: string): Observable<string> {
    return this.http
      .get(url, { responseType: 'text' })
      .pipe(switchMap((stored) => from(decodeMessageBody(stored))));
  }

  /** Build/version banner in the footer. */
  getVersion(): Observable<{ name: string; version: string }> {
    return this.http.get<{ name: string; version: string }>(`${this.base}/version`);
  }

  /**
   * Headline counters for the landing page, shown before login: global totals
   * (the last 30 days) from the tracer's one public read endpoint, as v1's
   * /stats was. Not derived from /v2/sessions — that needs a login and would
   * only count one page.
   */
  getTotals(): Observable<{ sessions: number; requests: number }> {
    return this.http.get<{ sessions: number; requests: number }>(`${this.base}/v2/totals`);
  }

  // ------------------------------------------------------------------- stats

  /**
   * Stats for one dimension over a window.
   *
   * The API defaults to 7 days and refuses anything wider than 90 — these are
   * the O(n) queries, so an unbounded range is not offered.
   */
  getStats(
    dimension: StatDimension,
    opts: { from?: string; to?: string; limit?: number } = {},
  ): Observable<StatsResponse> {
    let params = new HttpParams();
    if (opts.from) params = params.set('from', opts.from);
    if (opts.to) params = params.set('to', opts.to);
    if (opts.limit) params = params.set('limit', String(opts.limit));
    return this.http.get<StatsResponse>(`${this.base}/v2/stats/${dimension}`, {
      params,
      withCredentials: true,
    });
  }
}

/**
 * The response shape v1's message viewer expects.
 *
 * v1 served bodies through the API with a three-tier size protocol (inline,
 * presigned URL, or "too large — use the ZIP"). v2 serves them straight from
 * object storage, so the body always comes back and the tiering fields are
 * inert. They are kept so the existing viewer logic compiles unchanged.
 */
export interface V1MessageResponse {
  body: string;
  size: number;
  zip_only: boolean;
  display_on_demand: boolean;
  presigned_url: string;
  warning: string;
  headers: Record<string, string>;
  error?: string;
}

// ---------------------------------------------------------------- wire types

interface V2BodyLink {
  url: string;
  bytes: number;
}

interface V2SessionListItem {
  session_id: string;
  requests?: V2Inbound[];
  last_seen: string;
  request_count: number;
  error_count: number;
  warning_count: number;
  status: Session['status'];
  applications: string[];
  consumers: string[];
  agencies?: string[];
  methods: string[];
  providers: string[];
}

interface V2Outbound {
  seq: number;
  created_at: string;
  provider: string;
  provider_method: string;
  label?: string;
  status: string;
  error_kind?: string;
  duration_ms: number;
  http_status: number;
  url: string;
  http_method: string;
  bodies: Record<string, V2BodyLink>;
  // Per role, masked. Absent on rows traced before headers were recorded.
  headers?: Record<string, Record<string, string>>;
}

interface V2Inbound {
  request_id: string;
  created_at: string;
  method: string;
  status: string;
  // Our own answer plus the airline legs (an OK answer whose airline calls all
  // failed is "error"): what the row is coloured with, and what the session
  // status rolls up. Absent from tracers older than 62afb18.
  aggregate_status?: string;
  duration_ms: number;
  http_status: number;
  // The request line of the call to our API. Absent on rows traced before the
  // tracer stored it.
  http_method?: string;
  url?: string;
  consumer: string;
  application: string;
  agency: string;
  agent: string;
  providers: string;
  bodies: Record<string, V2BodyLink>;
  headers?: Record<string, Record<string, string>>;
  outbound: V2Outbound[];
}

interface V2SessionTree {
  session_id: string;
  requests: V2Inbound[];
}

// ------------------------------------------------------------------ adapters

function toSession(s: V2SessionListItem): Session {
  return {
    id: 0,
    value: s.session_id,
    created_at: s.last_seen,
    updated_at: s.last_seen,
    // The list carries a BOUNDED preview (newest 10 per session) so a row can
    // expand without a second round trip — same as v1. The full set lives
    // behind the session detail view.
    requests: (s.requests ?? []).map((r, i) => toRequest(r, i)),
    important: false,
    status: s.status,
    request_count: s.request_count,
    error_count: s.error_count,
    warning_count: s.warning_count,
    applications: s.applications,
    consumers: s.consumers,
    agencies: s.agencies,
    methods: s.methods,
    providers: s.providers,
  };
}

/**
 * Map one v2 inbound request (with nested airline calls) onto the flat
 * Request/Traces shape the templates expect.
 *
 * The inbound leg becomes a single 'json-api' trace carrying input/output; each
 * airline call becomes a 'requester' trace. Source is synthesised here — in v2
 * the nesting is authoritative, so nothing has to guess.
 */
function toRequest(r: V2Inbound, index: number): Request {
  // Synthetic ids, all >= 1: the views test ids for truthiness (`if (!id)`),
  // so an id of 0 — the first request's inbound trace and its first message —
  // read as "nothing selected" and its body was never fetched.
  const id = index + 1;
  const base = id * 1000;
  const inboundTrace: Trace = {
    ID: base,
    RequestID: id,
    Providers: r.providers,
    Status: r.status,
    Source: 'json-api',
    Messages: bodiesToMessages(
      r.bodies,
      r.headers,
      base,
      ['input', 'output'],
      r.created_at,
      r.duration_ms,
    ),
    CreatedAt: r.created_at,
    DurationMs: r.duration_ms,
    HttpStatus: r.http_status,
    Url: r.url,
    HttpMethod: r.http_method,
  };

  const legTraces: Trace[] = (r.outbound ?? []).map((o, i) => ({
    ID: base + i + 1,
    RequestID: id,
    Providers: o.provider,
    Status: o.status,
    Source: 'requester',
    Label: o.label,
    Order: o.seq,
    CreatedAt: o.created_at,
    ProviderMethod: o.provider_method,
    Url: o.url,
    HttpMethod: o.http_method,
    DurationMs: o.duration_ms,
    HttpStatus: o.http_status,
    ErrorKind: o.error_kind as Trace['ErrorKind'],
    Messages: bodiesToMessages(
      o.bodies,
      o.headers,
      base + i + 1,
      ['requester_input', 'provider_input', 'provider_output', 'requester_output'],
      o.created_at,
      o.duration_ms,
    ),
  }));

  return {
    ID: id,
    Application: r.application,
    Consumer: r.consumer,
    RequestID: r.request_id,
    SessionID: 0,
    Method: r.method,
    ProviderMethod: '',
    Agent: r.agent,
    Agency: r.agency,
    IsCollapsed: false,
    Traces: [inboundTrace, ...legTraces],
    CreatedAt: r.created_at,
    Provider: r.providers,
    Status: r.status,
    AggregateStatus: (r.aggregate_status || r.status) as Request['AggregateStatus'],
    DurationMs: r.duration_ms,
    HttpStatus: r.http_status,
  };
}

/** Narrows a session zip to one airline, or to the airline files only. */
export interface SessionZipFilter {
  provider?: string;
  scope?: 'airline';
}

/** The roles whose headers the tracer records (ingest.HeaderRoles). */
export const HEADER_ROLES = new Set(['input', 'provider_input', 'provider_output']);

/** Roles that are the answer half of a call: timed at its end, not its start. */
const RESPONSE_ROLES = new Set(['output', 'provider_output', 'requester_output']);

/**
 * When a call ended: its start plus its duration, as an ISO instant. Empty when
 * the start is unknown.
 */
export function completedAt(createdAt: string | undefined, durationMs: number | undefined): string {
  if (!createdAt) return '';
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return '';
  return new Date(t + (durationMs || 0)).toISOString();
}

/**
 * Turn a v2 `bodies` map into Messages, in a fixed request→response order so
 * the panes always appear in a sensible sequence rather than object-key order.
 *
 * Messages are not timed individually: a request message is stamped with the
 * call's start and a response message with its end (start + duration). A role
 * with headers but no body (its body could not be captured) still gets a
 * message, so its headers can be shown.
 */
function bodiesToMessages(
  bodies: Record<string, V2BodyLink> | undefined,
  headers: Record<string, Record<string, string>> | undefined,
  traceId: number,
  order: string[],
  createdAt: string,
  durationMs: number,
): Message[] {
  const out: Message[] = [];
  order.forEach((role, i) => {
    const link = bodies?.[role];
    const h = headers?.[role];
    if (!link?.url && !h) return;
    out.push({
      ID: traceId * 10 + i + 1,
      TraceID: traceId,
      Name: role,
      // Not carried on the wire: unknown until the body loads, when the viewer
      // sets it from the content (sniffBodyFormat).
      Format: '',
      CreatedAt: RESPONSE_ROLES.has(role) ? completedAt(createdAt, durationMs) : createdAt,
      BodyUrl: link?.url,
      Bytes: link?.bytes,
      Headers: h,
      HeadersKept: HEADER_ROLES.has(role),
    });
  });
  return out;
}
